import React, { useState, useEffect, lazy, Suspense, useContext, useRef } from "react";
import { UserDataContext } from "../../App";
import { Typography, Box, Input, InputLabel, Divider, FormControl, Select, MenuItem, IconButton, Dialog, useMediaQuery, DialogContent, Button, Tooltip } from "@mui/material";
import { ArrowDownII, Warning } from "../../assets/images/icons";
import REST, { flipRules } from "../../libs/API";
import ChoosePsDesctop from "./ChoosePsDesctop";
import ChoosePsMobile from "./ChoosePsMobile";
import OrderFormFields from "./OrderFormFields";
import { calc } from "../../libs/calc";
import { getText, isEqual, getRoundedFromSum, getRoundedDirSum, roundSumWithMultiple, roundSumWithMultipleBetweenLimits, currencyNetf } from "../../libs/utils";
import { useNavigate, useParams } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import { WhiteBlock } from "../../libs/theme";
import Notice from "./Notice";
const Sidebar = lazy(() => import('./Sidebar'));

const MyContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  " > div:only-child": {
    marginLeft: 'auto'
  },
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column'
  }
}))

const ChangedWhiteBlock = styled(WhiteBlock)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    width: '680px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '31px 30px'
  },
  [theme.breakpoints.only('xs')]: {
    padding: '20px',
    position: 'relative',
  }
}))

const ChooseCrypto = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.greyBg,
  borderRadius: '40px',
  margin: '2px 0 10px',
  display: 'inline-flex',
  width: '100%',
  [theme.breakpoints.only('xs')]: {
    flexDirection: 'column',
    borderRadius: '24px'
  },
  ".label": {
    borderRadius: '40px 0 0 40px',
    background: '#EAEAEA',
    fontSize: '12px',
    fontWeight: 800,
    width: '152px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
    [theme.breakpoints.only('xs')]: {
      display: 'none'
    }
  },
  ".select": {
    width: '57%',
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px 0 16px',
    [theme.breakpoints.down('lg')]: {
      width: '40%'
    },
    [theme.breakpoints.down('md')]: {
      width: '52%'
    },
    [theme.breakpoints.only('xs')]: {
      width: '100%',
      padding: '16px 10px 1px'
    },
    " > .MuiFormControl-root": {
      position: 'relative',
      top: '10px'
    }
  },
  ".count": {
    width: '43%',
    fontSize: '20px',
    padding: '16px 0',
    [theme.breakpoints.down('lg')]: {
      width: '60%'
    },
    [theme.breakpoints.down('md')]: {
      width: '48%'
    },
    [theme.breakpoints.only('xs')]: {
      width: '100%',
      padding: '9px 0',
      fontSize: '30px'
    },
  }
}))

const ChooseBottom = styled(Box)(({ theme }) => ({
  display: 'flex',
  fontWeight: 600,
  width: '100%',
  margin: '6px 0 28px',
  [theme.breakpoints.down('lg')]: {
    justifyContent: 'center'
  },
  [theme.breakpoints.only('xs')]: {
    flexWrap: 'wrap',
    justifyContent: 'left',
    gap: '15px',
    margin: '9px 0 18px',
  },
  "& > div": {
    width: '33.3%',
    fontSize: '14px',
    padding: '0 5px',
    [theme.breakpoints.down('lg')]: {
      width: '29%'
    },
    [theme.breakpoints.only('xs')]: {
      width: 'auto'
    },
    ".caption": {
      display: 'block',
      fontSize: '12px',
      color: theme.palette.primary.grey,
      fontWeight: '400  '
    }
  }
}))

const ArrowBotton = styled(IconButton)(() => ({
  position: 'relative',
  padding: '5px 25px 5px 5px',
  margin: '0 18px 0 0',
  "&::after": {
    content: "''",
    width: '12px',
    height: '6px',
    position: 'absolute',
    top: '50%',
    right: '0px',
    transform: 'translate(0, -50%)',
    background: 'url("/images/arrow-bottom.svg") center no-repeat',
    backgroundSize: 'contain',
    transition: 'transform 0.2s',
  },
  "&.active::after": {
    transform: 'translate(0, -50%) rotate(180deg)',
  }
}))

const Home = (props) => {
  let directWasChanged = useRef(true);
  const userData = useContext(UserDataContext);
  let hashTimer;
  const navigate = useNavigate();
  const theme = useTheme();
  let isXS = useMediaQuery(theme.breakpoints.only('xs'));
  let isSM = useMediaQuery(theme.breakpoints.only('sm'));
  const { tradeParams } = useParams();
  const [loading, setLoading] = useState(false);
  const [openGiveDialog, setOpenGiveDialog] = useState(false);
  const [openRecieveDialog, setOpenRecieveDialog] = useState(false);
  const [openRateDialog, setOpenRateDialog] = useState(false);

  const [paySystemsFrom, setPaySystemsFrom] = useState([]);
  const [fromSystem, setFromSystem] = useState({});
  const [fromCurrency, setFromCurrency] = useState({});
  const [fromWarning, setFromWarning] = useState({ id: '', text: '' });
  const [fromWallets, setFromWallets] = useState([]);
  const [fromMultipleTooltip, setFromMultipleTooltip] = useState(false);
  const [calcWarnings, setCalcWarnings] = useState({ from: '', to: '' });

  const [paySystemsTo, setPaySystemsTo] = useState([]);
  const [toSystem, setToSystem] = useState({});
  const [toCurrency, setToCurrency] = useState({});
  const [toWallets, setToWallets] = useState([]);
  const [toMultipleTooltip, setToMultipleTooltip] = useState(false);

  // lastHash, rate, rateValues, precisions, operation, fromMultiple, toMultiple, limits, fromSum - just prop to calc function
  const [currentRate, setCurrentRate] = useState(null);
  // fromSum, toSum, fromSumOrig, toSumOrig, fixPrec, isReverse
  const [amounts, setAmounts] = useState({ fromSum: '', toSum: '' });

  const [formData, setFormData] = useState({ terms: false });


  useEffect(() => {
    loadPaySystemsFrom();
    return () => clearTimeout(hashTimer);
  }, []);

  useEffect(() => {
    // Don't start on first load
    if (!props.greenScreen) {
      directWasChanged.current = true;
      loadPaySystemsTo(fromCurrency.id);
    }
  }, [fromCurrency]);

  useEffect(() => {
    // prefill formdata to use spb by default
    if (Object.keys(toCurrency).length > 0) {
      let initialFormData = {};
      [...toCurrency.page?.inputs, ...fromCurrency.inputs, ...toCurrency.inputs, ...paySystemsTo.inputs].map(item => {
        if (item.value != null) {
          initialFormData[item.name] = item.value;
        }
      })
      setFormData({ ...initialFormData });
    }

    directWasChanged.current = true;
    userData.token && getUserWallets();
  }, [toCurrency]);

  useEffect(() => {
    if (currentRate && !toSystem?.isExternal) {
      handleCalc({
        target: {
          id: "fromSum",
          value: currentRate.fromSum,
          fixPrec: true,
        }
      });
    }
  }, [currentRate]);

  useEffect(() => {
    amounts?.fixPrec && fixCalc();
  }, [amounts]);

  const debounceTimer = useRef(null);


  const getUrlContent = (url) => {
    return REST.getSiteStatus(url).then((data) => {
      if (data && data.success) {
        return data.data.exchangeAttributes
      }
    })
  }

  const loadPaySystemsFrom = async () => {
    const urlData = props.urlData || (tradeParams ? await getUrlContent(tradeParams) : null);

    const paySystemsFrom = await REST.getFromSystems().then(data => {
      if (!data.success) { return false }
      return data.data;
    });
    let fromSystemData, fromCurrencyData, paySystemsTo, toSystemData, toCurrencyData;

    if (urlData && urlData.fromPaymentSystemId) {
      // SELECT BY URL DATA
      // - FROM
      fromSystemData = paySystemsFrom.find(item => Number(urlData.fromPaymentSystemId) === Number(item.id));
      fromCurrencyData = fromSystemData.currencies.find(item => Number(urlData.fromCurrencyId) === Number(item.id));
      // - TO
      paySystemsTo = await REST.getSystemsWithProp(urlData.fromPaymentSystemId, urlData.fromCurrencyId);
      toSystemData = paySystemsTo.directions.find(item => Number(urlData.toPaymentSystemId) === Number(item.id));
      toCurrencyData = toSystemData.currencies.find(item => Number(urlData.toCurrencyId) === Number(item.id));

    } else {
      // SELECT BY DEFAULT
      // - FROM
      fromSystemData = paySystemsFrom[0];
      fromCurrencyData = fromSystemData.currencies[0];
      // - TO
      paySystemsTo = await REST.getSystemsWithProp(paySystemsFrom[0].id, paySystemsFrom[0].currencies[0].id);
      toSystemData = paySystemsTo.directions[0];
      toCurrencyData = toSystemData.currencies[0];
    }
    //  -- Sort currencies alphabetical order
    // fromSystemData.currencies.sort((a, b) => a.name.localeCompare(b.name));
    // toSystemData.currencies.sort((a, b) => a.name.localeCompare(b.name));
    // Save all on first page load
    setPaySystemsFrom(paySystemsFrom);
    setFromSystem(fromSystemData);
    setFromCurrency(fromCurrencyData);
    setPaySystemsTo(paySystemsTo);
    setToSystem(toSystemData);
    setToCurrency(toCurrencyData);
    updateRate(toCurrencyData.page)
  };

  const loadPaySystemsTo = async (currencyId) => {
    fromSystem.id && await REST.getSystemsWithProp(fromSystem.id, currencyId)
      .then((data) => {
        if (data?.directions) {
          setPaySystemsTo(data);

          let toSystemData = data.directions.find(item => Number(item.id) === Number(toSystem.id));
          if (!toSystemData) toSystemData = data.directions[0];

          updateToSystem(toSystemData);
        } else {
          window.showMessage("Направление обмена не найдено.");
        }

      });
  };

  const updateToSystem = (system) => {
    let defaultCurrency = system.currencies.find(item => Number(item.id) === Number(toCurrency.id));
    if (!defaultCurrency) defaultCurrency = system.currencies[0];
    // currency alphabetical sort 
    // system["currencies"].sort(function (a, b) {
    //   return a.name.localeCompare(b.name);
    // });

    setToSystem(system);
    setToCurrency(defaultCurrency);
    // calculate default rate
    updateRate(defaultCurrency.page, system);
  };

  const handleSelectFromCurrency = (event) => {
    const currencyId = event.target.value;
    setFromCurrency(fromSystem.currencies.find((item) => item.id === currencyId));
    //loadPaySystemsTo(currencyId);
  };

  const handleSelectToCurrency = (event) => {
    const id = event.target.value;
    const currency = toSystem.currencies.find((item) => item.id === id);
    updateRate(currency.page);
    setToCurrency(currency);
  };

  const updateRate = async (rate, toSystemData = {}) => {
    // change url
    navigate(`/deal/${rate.url}`);

    await REST.getCalcRate(rate.url).then((result) => {
      if (result.success) {
        const { data } = result;
        setCurrentRate({
          lastHash: data.hash,
          rate: data.rate,
          rateValues: data.rateValues,
          precisions: {
            from: data.fromPrecision,
            to: data.toPrecision
          },
          operation: data.operation,
          fromMultipleWithRecount: data?.fromMultipleWithRecount || 0,
          toMultipleType: data?.toMultipleType || 'up',
          toMultiple: data?.toMultiple || 0,
          fromMultiple: data?.fromMultiple || 0,
          limits: data.amountInput,
          fromSum: amounts.fromSum > data.amountInput.min * 1 ? amounts.fromSum : data.amountInput.min * 1,

        });
        if (toSystemData?.isExternal) {
          const value = data && data.calcValues && data.calcValues.fromAmount
          const toAmount = data && data.calcValues && data.calcValues.toAmount
          setAmounts({
            ...amounts,
            fromSum: value,
            fromSumOrig: null,
            toSum: toAmount,
            isReverse: 0,
            fixPrec: false
          });
        }
        clearTimeout(hashTimer);
        hashTimer = setTimeout(() => hashEndOfLife(), 1000 * data.hashLifetime);
      } else {
        window.showMessage(result.data && result.data[0].message);
      }
    });
    props.setGreenScreen(false);
  };

  const getUserWallets = () => {
    if (toSystem.id && toCurrency.id) {
      REST.getWalletList(userData.token, fromSystem.id, fromCurrency.id).then(result => {
        if (result.success && result.data.length) {
          setFromWallets(result.data);
        } else {
          setFromWallets([]);
        }
      });
      REST.getWalletList(userData.token, toSystem.id, toCurrency.id).then(result => {
        if (result.success && result.data.length) {
          setToWallets(result.data);
        } else {
          setToWallets([]);
        }
      });
    }
  }

  const calculator = (direction, value) => {
    if (currentRate) {
      const { rate, precisions, operation } = currentRate;
      return calc({ direction, value, rate, precisions, operation });
    }
  }

  const handleCalc = async (event, round = true) => {
    let { id, value, fixPrec } = event.target;

    console.log(value);

    if (typeof value === 'string') value = value.replace(',', '.');

    if (isNaN(value) || `${value}`.length > 14) return false;

    if (toSystem?.isExternal) {
      if (id == "fromSum") {
        return performExternalCalc(value);
      } else {
        return false // block TO amount value changes 
      }
    }


    console.log('calc', id, value, round);
    let amountResult = calculator(id, value);
    if (round) {
      console.log('go round', amountResult);
      amountResult = userRounding(amountResult);
    }
    setAmounts({ ...amountResult, fixPrec });
  };

  const fixCalc = (event) => {
    const { fromSum, toSum } = amounts;
    const { limits, precisions, fromMultiple, fromMultipleWithRecount } = currentRate;

    if (precisions.to === 0) {
      let newSum = calculator("toSum", toSum); // CALC DIRECT TO FROM
      if (newSum.fromSum < limits.min * 1) { // IF FROM SUM LOWER THEN LIMIT
        let newFromSum = calculator("fromSum", limits.min); // RECALC FROM LIMIT
        let newToSum = calculator("toSum", newFromSum.toSum);
        if (newToSum.fromSum < limits.min * 1) { // IF RECALC SUM IS LOWER THEN LIMIT
          sendToCalc("toSum", newFromSum.toSum * 1 + 1);
        } else {
          sendToCalc("toSum", newFromSum.toSum);
        }
      } else if (newSum.fromSum * 1 > limits.max * 1) {
        let newFromSum = calculator("fromSum", limits.max); // RECALC FROM LIMIT
        let newToSum = calculator("toSum", newFromSum.toSum);
        if (newToSum.fromSum > limits.max * 1) { // IF RECALC SUM IS HIGHER THEN LIMIT
          sendToCalc("toSum", newFromSum.toSum * 1 - 1);
        } else {
          sendToCalc("toSum", newFromSum.toSum);
        }
      } else {
        sendToCalc("toSum", toSum);
      }

    } else if (precisions.from === 0 && isEqual(calculator("fromSum", fromSum).toSum, toSum)) {
      // Fix from calc with precision
      sendToCalc("fromSum", fromSum, true);
    } else if (fromSum * 1 > limits.max * 1) {
      sendToCalc("fromSum", limits.max, true);
      return { id: "fromSum", text: getText("exInputSumMaxAlert") + " " + limits.max + " " + fromCurrency.name };
    } else if (fromSum * 1 < limits.min * 1) {
      console.log("fromSum < limits.min", fromSum, limits.min);
      if (getRoundedFromSum(fromSum, fromMultiple) < limits.min) {
        sendToCalc("fromSum", limits.min * 1 + fromMultiple, true);
      } else {
        sendToCalc("fromSum", limits.min, true);
      }
      return { id: "fromSum", text: getText("exInputSumMinAlert") + " " + limits.min + " " + fromCurrency.name };
    }

    if (fromMultipleWithRecount != 0 && fromSum != roundSumWithMultiple(fromSum, fromMultipleWithRecount)) {
      sendToCalc("fromSum", roundSumWithMultipleBetweenLimits(fromSum, fromMultipleWithRecount, limits.min, limits.max), true);
      return { id: "fromSum", text: getText("exInputSumMultypleAlert") + " " + fromMultipleWithRecount + " " + fromCurrency.name };
    }

    return true;
  }

  const userRounding = (data) => {
    let tempData = { ...data };
    const { fromSum, toSum } = data;
    const { fromMultiple, toMultiple, toMultipleType, fromMultipleWithRecount } = currentRate;

    console.log("call userRounding", fromSum, toSum, fromMultiple, toMultiple);

    if (!(fromMultipleWithRecount !== 0) && fromMultiple != 0) {
      tempData = {
        ...tempData,
        fromSum: getRoundedFromSum(fromSum, fromMultiple),
        fromSumOrig: fromSum,
      };
    } else {
      tempData = {
        ...tempData,
        fromSumOrig: null,
      };
    }

    console.log(tempData);

    if (toMultiple != 0) {
      tempData = {
        ...tempData,
        toSum: getRoundedDirSum(toSum, toMultiple, toMultipleType),
        toSumOrig: toSum,
      };
    } else {
      tempData = {
        ...tempData,
        toSumOrig: null,
      };
    }
    return tempData;
  }

  const handleLazyCalc = (value) => {
    REST.getCalcAmount(toCurrency?.page.url, value).then(data => {
      if (data.success) {
        const toAmount = data.data && data.data.calcValues && data.data.calcValues.toAmount
        setAmounts({
          ...amounts,
          fromSum: value,
          fromSumOrig: null,
          toSum: toAmount,
          isReverse: 0,
          fixPrec: false
        });
        setCurrentRate({
          ...currentRate,
          rateValues: data.data.rateValues
        })
      }
    });
  }

  const performExternalCalc = (value) => {
    const { limits } = currentRate;

    setAmounts({ ...amounts, fromSum: value, fixPrec: false });
    debounceTimer.current && clearTimeout(debounceTimer.current);
    if (Number(value) >= Number(limits.min) && Number(value) <= Number(limits.max)) {
      debounceTimer.current = setTimeout(() => handleLazyCalc(value), 700);
    }

  }

  const sendToCalc = (field, value, fix) => {
    console.log("call sendToCalc", field, value, fix);
    handleCalc({
      target: {
        id: field,
        value: value,
        fixPrec: fix,
      }
    });
  }

  const handleSubmitOrder = () => {
    const { isReverse, toSumOrig, toSum, fromSumOrig, fromSum } = amounts;
    const { lastHash } = currentRate;
    let amount = isReverse ? toSumOrig || toSum : fromSumOrig || fromSum;
    var submitData = {
      amount: amount,
      isReverse: isReverse,
      hash: lastHash
    };

    if (userData.email && userData.token) {
      Object.assign(submitData, { clientEmail: userData.email });
    }

    if (userData.refCode) {
      submitData.refCode = userData.refCode;
    }

    setLoading(true);

    Object.keys(formData).forEach(key => {
      if (formData[key] === "") {
        delete formData[key];
      } else {
        submitData[key] = formData[key];
      }
    });

    try {
      REST.createOrder(toCurrency.page.url, submitData).then(data => {
        if (data.success) {
          navigate('/status/' + data.data.code);
          window.scroll({ top: 0, behavior: 'smooth' })
        } else {
          if (data.data && data.data[0] && data.data[0].message) {
            window.showMessage(data.data[0].message);
          } else {
            window.showMessage("Ошибка сервера, попробуйте еще раз");
          }
        }
        setLoading(false);
      });
    } catch (error) {
      window.showMessage("Ошибка сервера, попробуйте еще раз");
      setLoading(false);
    }
  }

  const onBlur = (e) => {
    let result = fixCalc(e);
    if (typeof result === 'object') {
      setFromWarning({ id: result.id, text: result.text });
      setTimeout(() => setFromWarning({ id: '', text: '' }), 5000);
    } else {
      let amountResult = userRounding(amounts);

      if (directWasChanged.current && amountResult.toSumOrig && (amountResult.toSum != amountResult.toSumOrig)) {

        if (currentRate.toMultipleType === "math") {
          setFromWarning({ id: "toSum", text: getText("exInputSumMultypleAlert") + " " + currentRate.toMultiple });
          setTimeout(() => setFromWarning({ id: '', text: '' }), 5000);
        } else {
          directWasChanged.current = false;
          setToMultipleTooltip(true);
          setTimeout(() => setToMultipleTooltip(false), 3000);
        }

      }

      if (directWasChanged.current && amountResult.fromSumOrig && (amountResult.fromSum != amountResult.fromSumOrig)) {
        directWasChanged.current = false;
        setFromMultipleTooltip(true);
        setTimeout(() => setFromMultipleTooltip(false), 3000);
      }

      setAmounts(amountResult)
    }
  }

  const hashEndOfLife = () => {
    setOpenRateDialog(true);
    //this.setState({ blured: true, lastHash: null })
  }

  const handleUpdateRateButton = () => {
    updateRate(toCurrency.page);
    setOpenRateDialog(false);
  }

  const handleLimitsCalc = value => {
    console.log("call handleLimitsCalc", value);
    handleCalc({
      target: {
        id: "fromSum",
        value: value,
        fixPrec: true
      }
    })
  }

  // Auth and KYC requirement logic
  const requireAuth = fromCurrency?.requireAuth ? !userData.token : false;
  const requireKYC = fromCurrency?.requireAuth && fromCurrency?.requireKyc && userData.token && !userData.hasKyc;
  const hasRequireForm = requireKYC || requireAuth;

  return (
    <MyContainer className="content">
      <Suspense><Sidebar /></Suspense>

      <ChangedWhiteBlock>
        <ChooseCrypto>
          <Box className="label">{getText('accOrdersThFrom')}</Box>

          <Box className="select">

            {isXS
              ? <ChoosePsMobile
                title={getText('orderFormToGive')}
                setOpenDialog={setOpenGiveDialog}
                openDialog={openGiveDialog}
                paySystems={paySystemsFrom}
                system={fromSystem}
                setSystem={setFromSystem}
                currency={fromCurrency}
                setCurrency={setFromCurrency}
              />
              : <ChoosePsDesctop
                title={getText('orderFormToGive')}
                setOpenDialog={setOpenGiveDialog}
                openDialog={openGiveDialog}
                paySystems={paySystemsFrom}
                system={fromSystem}
                setSystem={setFromSystem}
                currency={fromCurrency}
                setCurrency={setFromCurrency}
              />
            }

            <FormControl size="small" sx={{ flexGrow: 1 }}>
              <InputLabel id="crypto-network">{fromSystem.name}</InputLabel>
              <Select
                id="crypto-network"
                sx={{ width: 'fit-content' }}
                value={fromCurrency.id ?? ""}
                onChange={handleSelectFromCurrency}
                disabled={fromSystem.currencies?.length <= 1 ? true : false}
                MenuProps={{
                  MenuListProps: { sx: { maxHeight: '173px' } },
                  PaperProps: {
                    variant: "small",
                  },
                }}
              >
                {fromSystem.currencies?.map(({ id, name }, key) => (
                  <MenuItem
                    key={key}
                    value={id}
                    sx={{
                      display: fromCurrency.id == id ? 'none' : 'inherit',
                      color: isXS ? '#686868' : 'inherit'
                    }}
                  >
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider
            orientation={isXS ? 'horizontal' : 'vertical'}
            flexItem
            sx={{
              margin: { xs: '18px 15px 0', sm: '18px 0' }
            }}
          />

          <Box className="count">
            <Tooltip
              open={fromMultipleTooltip}
              placement="bottom-end"
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 10],
                    },
                  },
                ],
              }}
              title={<>
                <Typography variant='h5'>{getText('ocrRoundHead')}</Typography>
                <Typography variant='caption'>{getText('ocrRoundText')}</Typography>
              </>}
            >
              <Input
                fullWidth
                disableUnderline
                id="fromSum"
                type="text"
                value={amounts.fromSum}
                onBlur={(e) => onBlur(e)}
                onChange={(e) => handleCalc(e, false)}
                error={false}
                sx={{
                  margin: 0,
                  padding: "6px 20px",
                  fontSize: { xs: '30px', sm: '20px' },
                  color: fromWarning.id === 'fromSum' && '#BF5959'
                }}
              />
            </Tooltip>
          </Box>
        </ChooseCrypto>

        {fromWarning.id === 'fromSum' && (
          <Typography variant="warning" component="div">
            {fromWarning.text}
          </Typography>
        )}

        {isXS && <Box sx={{ width: '100%', textAlign: 'center', padding: '6px 0' }}><ArrowDownII /></Box>}

        <ChooseCrypto>
          <Box className="label">{getText('accOrdersThTo')}</Box>

          <Box className="select">

            {isXS
              ? <ChoosePsMobile
                title={getText('orderFormToRecieve')}
                setOpenDialog={setOpenRecieveDialog}
                openDialog={openRecieveDialog}
                paySystems={paySystemsTo.directions}
                system={toSystem}
                setSystem={updateToSystem}
                currency={toCurrency}
              />
              : <ChoosePsDesctop
                title={getText('orderFormToRecieve')}
                setOpenDialog={setOpenRecieveDialog}
                openDialog={openRecieveDialog}
                paySystems={paySystemsTo.directions}
                system={toSystem}
                setSystem={updateToSystem}
                currency={toCurrency}
              />
            }

            <FormControl size="small" sx={{ flexGrow: 1 }}>
              <InputLabel id="crypto-network-to">{toSystem.name}</InputLabel>
              <Select
                sx={{ width: 'fit-content' }}
                id="crypto-network-to"
                value={toCurrency.id ?? ""}
                onChange={handleSelectToCurrency}
                disabled={toSystem.currencies?.length <= 1 ? true : false}
                MenuProps={{
                  MenuListProps: { sx: { maxHeight: '173px' } },
                  PaperProps: {
                    variant: "small",
                  }
                }}
              >
                {toSystem.currencies?.map(({ id, name }, key) => (
                  <MenuItem
                    key={key}
                    value={id}
                    sx={{
                      display: toCurrency.id == id ? 'none' : 'inherit',
                      color: isXS ? '#686868' : 'inherit'
                    }}
                  >
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider
            orientation={isXS ? 'horizontal' : 'vertical'}
            flexItem
            sx={{
              margin: { xs: '18px 15px 0', sm: '18px 0' }
            }}
          />

          <Box className="count">
            <Tooltip
              open={toMultipleTooltip}
              placement="bottom-end"
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 10],
                    },
                  },
                ],
              }}
              title={<>
                <Typography variant='h5'>{getText('ocrRoundHead')}</Typography>
                <Typography variant='caption'>{getText('ocrRoundText')}</Typography>
              </>}
            >
              <Input
                fullWidth
                disableUnderline
                value={amounts.toSum}
                type="text"
                onBlur={(e) => onBlur(e)}
                onChange={(e) => handleCalc(e, false)}
                id="toSum"
                sx={{
                  margin: 0,
                  padding: "6px 20px",
                  fontSize: { xs: '30px', sm: '20px' },
                  color: fromWarning.id === 'toSum' && '#BF5959'
                }}
              />
            </Tooltip>
          </Box>
        </ChooseCrypto>

        {fromWarning.id === 'toSum' && (
          <Typography variant="warning" component="div">
            {fromWarning.text}
          </Typography>
        )}

        <ChooseBottom>
          <Box className="item">
            {currentRate && `${currentRate.rateValues.fromValue} ${currentRate.rateValues.fromCurrency} = ${currentRate.rateValues.toValue} ${currentRate.rateValues.toCurrency}`}
            <span className="caption">{getText('accOrderRateData')}</span>
          </Box>
          <Box className="item">
            {currentRate && <Box sx={{ cursor: 'pointer' }} onClick={() => handleLimitsCalc(currentRate.limits.min)}>
              {`${currentRate.limits.min} ${currentRate.rateValues.fromCurrency}`}
            </Box>}
            <span className="caption">{getText('rateMin')}</span>
          </Box>
          <Box className="item">
            {currentRate && <Box sx={{ cursor: 'pointer' }} onClick={() => handleLimitsCalc(currentRate.limits.max)}>
              {`${currentRate.limits.max} ${currentRate.rateValues.fromCurrency}`}
            </Box>}
            <span className="caption">{getText('rateMax')}</span>
          </Box>
        </ChooseBottom>

        {hasRequireForm && <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr' },
          gap: '20px',
          marginTop: '24px',
          "& > *": {
            height: 'fit-content',
            marginTop: '0px !important',
          }
        }}>
          <Notice
            type="warning"
            icon={<Warning />}
          >
            <Typography variant="h5" component="h4" sx={{ mb: '4px', mt: '8px' }}>
              {requireKYC ? getText('authKycReqHeader') : getText('woAuthKycReqHeader')}
            </Typography>
            <Typography sx={{
              marginBottom: '6px',
            }}>
              {requireKYC ? getText('authKycReqText') : getText('woAuthKycReqText')}
            </Typography>
          </Notice>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '8px',
            "& > *": {
              margin: '0px !important',
            }
          }}>
            {
              requireKYC ?
                <Box textAlign={'right'} width={'100%'}>
                  <Button variant="contained" size='large'
                    fullWidth
                    sx={{ maxWidth: { sm: '250px', xs: '100%' } }}
                    className='action' href="/kyc-pass">{getText('goToKyc')}</Button>
                </Box>
                : <>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    className='action'
                    onClick={() => openHeaderForm('AuthNoRedirect')}
                  >
                    {getText('goToAuth')}
                  </Button>
                  <Button
                    fullWidth
                    color="info"
                    size="large"
                    className='light'
                    variant="contained"
                    onClick={() => openHeaderForm('Register')}
                  >
                    {getText('goToRegister')}
                  </Button>
                </>
            }
          </Box>
        </Box>}

        {!hasRequireForm && (fromCurrency.inputs && toCurrency.inputs) && (
          <OrderFormFields
            className="order-form"
            fields={flipRules([
              ...(toSystem?.needFromField ? paySystemsTo.inputs : []), // from inputs keeps in paySystemsTo.inputs
              ...toCurrency?.page.inputs,
              ...toCurrency?.inputs,
            ])}
            notices={[paySystemsTo.fromNotice, toCurrency.toNotice]}
            handleSubmitOrder={handleSubmitOrder}
            toSystem={toSystem}
            systemsType={{
              from: fromSystem?.type === 0 ? (fromCurrency?.code === "RUB" ? 0 : 100) : fromSystem?.type,
              to: toSystem?.type === 0 ? (toCurrency?.code === "RUB" ? 0 : 100) : toSystem?.type
            }}
            formData={formData}
            setFormData={setFormData}
            toWallets={toWallets}
            fromWallets={fromWallets}
            loading={loading}
            directKey={toCurrency?.page?.url}
          />
        )}
      </ChangedWhiteBlock>

      <Dialog
        open={openRateDialog}
        maxWidth="sm"
      >
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="h4">{getText('rateRefreshH1')}</Typography>
          <Typography variant="caption" sx={{ display: 'inline-block', margin: '8px 0 20px' }}>
            {getText('rateRefreshText')}
          </Typography>
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ paddingTop: '16px', paddingBottom: '16px' }}
            onClick={handleUpdateRateButton}
          >
            {getText('rateRefreshButton')}
          </Button>
        </DialogContent>
      </Dialog>
    </MyContainer>
  );
};

export default Home;
